import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Wrench, IndianRupee, School, Award, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHero, Reveal } from "@/components/Reveal";
import { api, fmtINR } from "@/lib/api";
import { IMG } from "@/lib/content";
import { useLang } from "@/context/LanguageContext";

const medal = ["bg-[#ffb800] text-[#0A192F]", "bg-[#E2E8F0] text-[#0A192F]", "bg-[#cd7f32] text-[#0A192F]"];

const Board = ({ title, icon: Icon, rows, metric, testid, empty }) => (
  <Reveal>
    <div className="bg-white shadow-[0_8px_30px_rgba(10,25,47,0.06)] border border-slate-200 rounded-2xl p-6 h-full" data-testid={testid}>
      <h2 className="flex items-center gap-2 text-xl font-bold"><Icon className="w-5 h-5 text-[#007A8A]" />{title}</h2>
      {rows.length === 0 ? <p className="text-slate-500 text-sm mt-4">{empty}</p> : (
        <ol className="mt-4 space-y-2">
          {rows.map((r, i) => (
            <li key={i} className="flex items-center gap-3 p-3 rounded-xl bg-[#F0F4F8] hover:bg-slate-100 transition-colors" data-testid={`${testid}-row-${i}`}>
              <span className={`w-8 h-8 rounded-full grid place-items-center text-sm font-black shrink-0 ${medal[i] || "bg-slate-900/[0.07] text-[#0A192F]"}`}>{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate flex items-center gap-1">{r.name || r.school_name}{r.certified && <Award className="w-3.5 h-3.5 text-[#FF2E63]" />}</div>
                <div className="text-xs text-slate-500 truncate">{r.name ? `Class ${r.class_grade} · ${r.program} · ${r.school_name}` : `${r.city} · ${r.students} students`}</div>
              </div>
              <div className="font-extrabold font-['Outfit'] text-[#FF2E63] whitespace-nowrap">{metric(r)}</div>
            </li>
          ))}
        </ol>
      )}
    </div>
  </Reveal>
);

const lastMonths = () => Array.from({ length: 12 }, (_, i) => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - i); return d.toISOString().slice(0, 7); });
const monthLabel = (m, lang) => new Date(`${m}-01`).toLocaleDateString(lang === "hi" ? "hi-IN" : "en-IN", { month: "long", year: "numeric" });

const ChampionBanner = ({ c }) => {
  const { L, lang } = useLang();
  return (
    <Reveal>
      <div className="max-w-7xl mx-auto px-5 lg:px-8 mb-8">
        <div className="rounded-2xl p-6 bg-gradient-to-r from-[#FF2E63] to-[#FF2E63] text-white flex flex-wrap items-center gap-5" data-testid="champion-banner">
          <div className="w-16 h-16 rounded-full bg-white/20 grid place-items-center"><Trophy className="w-8 h-8" /></div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-[0.2em] font-bold">{L("Maker of the Month", "मेकर ऑफ़ द मंथ")} · {monthLabel(c.month, lang)}</div>
            <div className="text-3xl font-extrabold" data-testid="champion-name">{c.name}</div>
            <div className="text-white/85 text-sm">{L(`Class ${c.class_grade}`, `कक्षा ${c.class_grade}`)} · {c.program} · {c.school_name}</div>
          </div>
          <div className="text-right"><div className="text-3xl font-black font-['Outfit']">{c.projects}</div><div className="text-xs">{L("projects completed", "पूरे प्रोजेक्ट")}</div>
            <Link to="/champions" className="mt-2 inline-block text-xs font-bold underline" data-testid="leaderboard-wall-link">{L("Champion Wall →", "चैंपियन वॉल →")}</Link></div>
        </div>
      </div>
    </Reveal>
  );
};

export default function Leaderboard() {
  const { L, lang } = useLang();
  const [d, setD] = useState(null);
  const [month, setMonth] = useState("all");
  useEffect(() => { api.get("/leaderboard", { params: month === "all" ? {} : { month } }).then((r) => setD(r.data)); }, [month]);

  return (
    <div data-testid="leaderboard-page">
      <PageHero chip={L("Leaderboard", "लीडरबोर्ड")} title={L("Top makers. Top earners.", "शीर्ष मेकर्स। शीर्ष कमाने वाले।")}
        subtitle={L("Celebrating RoboSpark students across partner schools who build the most and earn from what they create.", "साझेदार स्कूलों के उन RoboSpark छात्रों का सम्मान जो सबसे ज़्यादा बनाते हैं और अपनी रचनाओं से कमाते हैं।")} image={IMG.robot} testid="leaderboard-hero">
        {d && (
          <div className="mt-8 flex flex-wrap gap-6 text-sm" data-testid="leaderboard-totals">
            <div><b className="text-2xl font-['Outfit']">{d.totals.students}</b><div className="text-slate-500">{L("active makers", "सक्रिय मेकर्स")}</div></div>
            <div><b className="text-2xl font-['Outfit']">{d.totals.projects}</b><div className="text-slate-500">{L("projects completed", "पूरे प्रोजेक्ट")}</div></div>
            <div><b className="text-2xl font-['Outfit']">{fmtINR(d.totals.earnings_inr)}</b><div className="text-slate-500">{L("earned by students", "छात्रों की कमाई")}</div></div>
          </div>
        )}
      </PageHero>
      <section className="bg-[#F0F4F8] text-[#0A192F] py-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 mb-8 flex flex-wrap gap-2" data-testid="leaderboard-month-filter">
          {["all", ...lastMonths()].map((m) => (
            <button key={m} onClick={() => setMonth(m)} data-testid={`lb-month-${m}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${month === m ? "bg-[#FF3B3B] border-[#FF2E63] text-white" : "border-slate-300 text-slate-700 hover:border-slate-500 hover:border-white"}`}>
              {m === "all" ? L("All time", "सर्वकालिक") : monthLabel(m, lang)}
            </button>
          ))}
        </div>
        {d?.champion && <ChampionBanner c={d.champion} />}
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {d && <>
            <Board title={L("Top Makers", "शीर्ष मेकर्स")} icon={Wrench} rows={d.makers} metric={(r) => L(`${r.projects} projects`, `${r.projects} प्रोजेक्ट`)} testid="board-makers" empty={L("No projects yet.", "अभी कोई प्रोजेक्ट नहीं।")} />
            <Board title={L("Top Earners", "शीर्ष कमाने वाले")} icon={IndianRupee} rows={d.earners} metric={(r) => fmtINR(r.earnings_inr)} testid="board-earners" empty={L("No earnings yet.", "अभी कोई कमाई नहीं।")} />
            <Board title={L("Top Schools", "शीर्ष स्कूल")} icon={School} rows={d.schools} metric={(r) => L(`${r.projects} projects`, `${r.projects} प्रोजेक्ट`)} testid="board-schools" empty={L("No schools yet.", "अभी कोई स्कूल नहीं।")} />
          </>}
        </div>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 mt-10 flex flex-wrap items-center justify-between gap-4">
          <p className="text-slate-500 text-sm flex items-center gap-2"><Trophy className="w-4 h-4 text-[#ffb800]" />{L("Only first names and last initials are shown to protect student privacy.", "छात्रों की निजता के लिए केवल पहला नाम और उपनाम का पहला अक्षर दिखाया जाता है।")}</p>
          <Link to="/enroll"><Button className="rs-cta rounded-full" data-testid="leaderboard-enroll-btn">{L("Join the leaderboard", "लीडरबोर्ड में शामिल हों")} <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
        </div>
      </section>
    </div>
  );
}
